import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// App.tsx
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import esES from 'antd/locale/es_ES';
import { ThemeProvider } from 'styled-components';
import { customTheme, LoadingSpinner } from '@customer-platform/shared';
import { applyCssVariables, createAntdTheme } from '@shared/theme';
import { GlobalStateProvider } from './contexts/GlobalStateContext';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import KeycloakTestPage from './pages/KeycloakTestPage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { isKeycloakEnabled, isAuthDisabled } from './config/auth.config';
import keycloakService from './services/KeycloakService';
import './styles/global.css';
import { ApiContext, configureApiMapper, createApiAdapter } from 'sysone-api-mapper';
import { InsureBlocksProvider } from "@sysone/insureblocks";
import { defaultApiConfig, axiosInstance } from "@sysone/components";
// Lazy load microfrontends
const ConsultaPolizaApp = React.lazy(() => import('consultaPoliza/ConsultaPolizaApp'));
const DenunciaSiniestrosApp = React.lazy(() => import('denunciaSiniestros/DenunciaSiniestrosApp'));
const CotizadoresApp = React.lazy(() => import('cotizadores/CotizadoresApp'));
const PagosApp = React.lazy(() => import('pagos/PagosApp'));
const OnboardingApp = React.lazy(() => import('onboarding/OnboardingApp'));
// ==================== API SETUP ====================
const setupApi = (tenant) => {
    const apiConfig = {
        baseURL: process.env.REACT_APP_API_BASE_URL || "https://app-dev.sysone.com/",
        headers: {
            "X-Tenant": tenant,
            "X-User": "linkone",
            "X-Api-Key": "PYRxE7iBUFCFcDmTwIFGEkvSMzJ88hSU4uWG0DL0ebk1CYVamsa6phIlsECQQMqkM0XXtZ5K1L85cyr3MgkIhtjlIh4niBsagiVRjzIaqNKxSU2N8Y7KaZLItDB2oDTc"
        }
    };
    configureApiMapper(apiConfig);
    // Limpiar interceptores anteriores
    axiosInstance.interceptors.request.handlers = [];
    axiosInstance.interceptors.request.use((config) => {
        config.baseURL = apiConfig.baseURL;
        config.headers["X-Tenant"] = apiConfig.headers["X-Tenant"];
        config.headers["X-User"] = apiConfig.headers["X-User"];
        config.headers["X-Api-Key"] = apiConfig.headers["X-Api-Key"];
        return config;
    }, (error) => Promise.reject(error));
    console.log(`✅ API Mapper configurado para tenant: ${tenant}`);
};
// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    if (isAuthDisabled())
        return _jsx(_Fragment, { children: children });
    if (isKeycloakEnabled()) {
        const hasCallback = window.location.hash.includes('code=') ||
            window.location.hash.includes('access_token') ||
            window.location.search.includes('code=');
        if (hasCallback) {
            return _jsx("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }, children: _jsx("div", { children: "Procesando autenticaci\u00F3n..." }) });
        }
        if (user && !isLoading)
            return _jsx(_Fragment, { children: children });
        if (isLoading)
            return _jsx(LoadingSpinner, { tip: "Verificando autenticaci\u00F3n..." });
        if (!isAuthenticated) {
            keycloakService.login();
            return _jsx(LoadingSpinner, { tip: "Redirigiendo a Keycloak..." });
        }
    }
    if (isLoading)
        return _jsx(LoadingSpinner, { tip: "Verificando autenticaci\u00F3n..." });
    return _jsx(_Fragment, { children: children });
};
// Microfrontend wrapper...
const MicrofrontendWrapper = ({ children, fallback }) => (_jsx(ErrorBoundary, { fallback: fallback, children: _jsx(Suspense, { fallback: _jsx(LoadingSpinner, { tip: "Cargando m\u00F3dulo..." }), children: children }) }));
// ==================== APP ====================
const App = () => {
    const { isAuthenticated, tenant, isLoading: authLoading } = useAuth();
    const [isAppReady, setIsAppReady] = useState(false);
    const [isApiConfigured, setIsApiConfigured] = useState(false);
    // Aplicar tema CSS
    useEffect(() => {
        applyCssVariables(customTheme);
    }, []);
    // Configuración del API
    useEffect(() => {
        if (!isAuthenticated || !tenant) {
            setIsApiConfigured(false);
            setIsAppReady(true);
            return;
        }
        try {
            setupApi(tenant);
            setIsApiConfigured(true);
            setIsAppReady(true);
            savePartyType();
        }
        catch (error) {
            console.error("Error configurando API Mapper:", error);
            setIsApiConfigured(false);
            setIsAppReady(true);
        }
    }, [isAuthenticated, tenant]);
    const savePartyType = async () => {
        //me fijo que tipo de party es y lo guardo para usar en otros lados de la app
        const partyCode = localStorage.getItem("user-party-code");
        if (partyCode && partyCode != undefined && partyCode != "undefined") {
            const res = await api.party.getPartyStatus(partyCode);
            const personType = res?.partyType;
            localStorage.setItem("party-person-type", personType);
        }
    };
    const api = useMemo(() => createApiAdapter(tenant ?? "sysone"), [tenant]);
    // Loading mientras no esté todo listo
    if (authLoading || !isAppReady || (isAuthenticated && !isApiConfigured)) {
        return _jsx(LoadingSpinner, { tip: "Configurando conexi\u00F3n con el servidor..." });
    }
    // Force rebuild to refresh deployed artifacts III
    return (_jsx(ErrorBoundary, { children: _jsx(InsureBlocksProvider, { theme: {
                colors: {
                    primary: customTheme.primaryColor,
                    secondary: customTheme.secondaryColor,
                    success: customTheme.successColor,
                    danger: customTheme.errorColor,
                    warning: customTheme.warningColor,
                    text: customTheme.textColor,
                    background: customTheme.backgroundColor,
                },
            }, apiConfig: defaultApiConfig, children: _jsx(ThemeProvider, { theme: customTheme, children: _jsx(ConfigProvider, { theme: createAntdTheme(customTheme), locale: esES, componentSize: "middle", children: _jsx(AntApp, { children: _jsx(ApiContext.Provider, { value: api, children: _jsx(GlobalStateProvider, { children: _jsx(Router, { children: _jsx(AppRoutes, {}) }) }) }) }) }) }) }) }));
};
// Main App Routes component
const AppRoutes = () => {
    return (_jsxs(Routes, { children: [_jsx(Route, { path: "/onboarding", element: _jsx(MicrofrontendWrapper, { fallback: _jsx("div", { style: { padding: '24px', textAlign: 'center' }, children: _jsx("h3", { children: "Error al cargar Onboarding" }) }), children: _jsx(OnboardingApp, {}) }) }), _jsx(Route, { path: "/login", element: _jsx(LoginPage, {}) }), _jsx(Route, { path: "/keycloak-test", element: _jsx(KeycloakTestPage, {}) }), _jsx(Route, { path: "/", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(DashboardPage, {}) }) }) }), _jsx(Route, { path: "/dashboard", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(DashboardPage, {}) }) }) }), _jsx(Route, { path: "/profile", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(ProfilePage, {}) }) }) }), _jsx(Route, { path: "/consulta-poliza/*", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(MicrofrontendWrapper, { fallback: _jsxs("div", { style: { padding: '24px', textAlign: 'center' }, children: [_jsx("h3", { children: "Error al cargar Consulta de P\u0E23\u0E13liza" }), _jsx("p", { children: "Por favor, intente nuevamente m\u0E23\u0E01s tarde." })] }), children: _jsx(ConsultaPolizaApp, {}) }) }) }) }), _jsx(Route, { path: "/denuncia-siniestros/*", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(MicrofrontendWrapper, { fallback: _jsxs("div", { style: { padding: '24px', textAlign: 'center' }, children: [_jsx("h3", { children: "Error al cargar Denuncia de Siniestros" }), _jsx("p", { children: "Por favor, intente nuevamente m\u0E23\u0E01s tarde." })] }), children: _jsx(DenunciaSiniestrosApp, {}) }) }) }) }), _jsx(Route, { path: "/cotizadores/*", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(MicrofrontendWrapper, { fallback: _jsxs("div", { style: { padding: '24px', textAlign: 'center' }, children: [_jsx("h3", { children: "Error al cargar Cotizadores" }), _jsx("p", { children: "Por favor, intente nuevamente m\u0E23\u0E01s tarde." })] }), children: _jsx(CotizadoresApp, {}) }) }) }) }), _jsx(Route, { path: "/pagos/*", element: _jsx(ProtectedRoute, { children: _jsx(MainLayout, { children: _jsx(MicrofrontendWrapper, { fallback: _jsxs("div", { style: { padding: '24px', textAlign: 'center' }, children: [_jsx("h3", { children: "Error al cargar Pagos" }), _jsx("p", { children: "Por favor, intente nuevamente m\u0E23\u0E01s tarde." })] }), children: _jsx(PagosApp, {}) }) }) }) }), _jsx(Route, { path: "*", element: _jsx(NotFoundPage, {}) })] }));
};
export default App;
